import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiUser, FiMail, FiPhone, FiMapPin, FiEdit3, FiSave, FiX, FiBookOpen, FiHeart, FiShoppingBag, FiUpload, FiFile, FiDownload, FiImage, FiBook, FiLogOut } from 'react-icons/fi';
import { authService } from '../services/authService';

const ProfileContainer = styled.div`
  min-height: 100vh;
  background-color: #f8fafc;
  padding: 40px 0;
`;

const ProfileContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
  justify-content: space-between;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
  
  &:hover {
    color: #2563eb;
  }
`;

const ProfileTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const ProfileSidebar = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  height: fit-content;
`;

const ProfileAvatar = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
  font-size: 2rem;
  font-weight: 700;
`;

const ProfileName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  text-align: center;
  margin-bottom: 8px;
`;

const ProfileEmail = styled.p`
  color: #6b7280;
  text-align: center;
  margin-bottom: 24px;
`;

const ProfileStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  
  .icon {
    color: #2563eb;
  }
  
  .content {
    flex: 1;
  }
  
  .label {
    font-size: 0.875rem;
    color: #6b7280;
  }
  
  .value {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
  }
`;

const ProfileMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ProfileSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  
  &:disabled {
    background-color: #f9fafb;
    color: #6b7280;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  
  &:disabled {
    background-color: #f9fafb;
    color: #6b7280;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const SignOutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 24px;
  
  &:hover {
    background: #dc2626;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  }
`;

const FileUploadContainer = styled.div`
  margin-top: 20px;
  padding: 20px;
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  text-align: center;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #2563eb;
    background: #f0f4ff;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FileInfo = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const FileName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #374151;
  font-weight: 500;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.span`
  color: #6b7280;
  font-size: 0.875rem;
`;

const DownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #2563eb;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #1d4ed8;
    transform: translateY(-1px);
  }
`;

const UploadStatus = styled.div`
  margin-top: 12px;
  padding: 12px;
  border-radius: 8px;
  font-size: 0.875rem;
  text-align: center;
  
  &.success {
    background: #d1fae5;
    color: #065f46;
  }
  
  &.error {
    background: #fee2e2;
    color: #991b1b;
  }
  
  &.uploading {
    background: #dbeafe;
    color: #1e40af;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #6b7280;
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 16px;
    color: #374151;
  }
  
  p {
    font-size: 1rem;
    margin-bottom: 24px;
  }
`;

const UserProfile = ({ user, isAuthenticated, onUserUpdate, onLogout }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    bio: '',
    bookTitle: '',
    authorName: '',
    genre: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadingBook, setUploadingBook] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [bookUploadStatus, setBookUploadStatus] = useState(null);
  const [coverUploadStatus, setCoverUploadStatus] = useState(null);
  const [uploadedBookFile, setUploadedBookFile] = useState(null);
  const [uploadedCoverImage, setUploadedCoverImage] = useState(null);
  const navigate = useNavigate();

  // Update formData when user prop changes
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zipCode: user.zipCode || '',
        country: user.country || '',
        bio: user.bio || '',
        bookTitle: user.bookTitle || '',
        authorName: user.authorName || '',
        genre: user.genre || '',
        description: user.description || ''
      });
      
      // Set uploaded file info if exists
      if (user.pdf_document) {
        setUploadedFile({
          path: user.pdf_document,
          name: user.pdf_document.split('/').pop()
        });
      }
      
      // Set uploaded book file if exists
      if (user.bookFile) {
        setUploadedBookFile({
          name: user.bookFile.split('/').pop(),
          path: user.bookFile
        });
      }
      
      // Set uploaded cover image if exists
      if (user.coverImage) {
        const coverPath = user.coverImage.startsWith('http') 
          ? user.coverImage 
          : `https://m2ebooks.com/php_backend/${user.coverImage}`;
        setUploadedCoverImage({
          name: user.coverImage.split('/').pop(),
          preview: coverPath
        });
      }
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Save book information
      const bookData = {
        bookTitle: formData.bookTitle,
        authorName: formData.authorName,
        genre: formData.genre,
        description: formData.description
      };
      
      await authService.saveBookInfo(bookData);
      
      // Update user object in localStorage and parent state
      const updatedUser = { 
        ...user, 
        bookTitle: formData.bookTitle,
        authorName: formData.authorName,
        genre: formData.genre,
        description: formData.description
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Update parent component state if callback provided
      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }
      
      setIsEditing(false);
      setLoading(false);
      alert('Book information saved successfully!');
    } catch (error) {
      setLoading(false);
      alert('Failed to save book information: ' + error.message);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zipCode: user.zipCode || '',
        country: user.country || '',
        bio: user.bio || '',
        bookTitle: user.bookTitle || '',
        authorName: user.authorName || '',
        genre: user.genre || '',
        description: user.description || ''
      });
    }
    setIsEditing(false);
  };

  const handleBookFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (PDF, EPUB, MOBI, etc.)
    const allowedTypes = ['application/pdf', 'application/epub+zip', 'application/x-mobipocket-ebook'];
    const allowedExtensions = ['pdf', 'epub', 'mobi'];
    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setBookUploadStatus({ type: 'error', message: 'Please upload a valid book file (PDF, EPUB, or MOBI).' });
      return;
    }

    // Validate file size (50MB max)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setBookUploadStatus({ type: 'error', message: 'File size exceeds 50MB limit.' });
      return;
    }

    setUploadingBook(true);
    setBookUploadStatus({ type: 'uploading', message: 'Uploading book file...' });

    try {
      // TODO: Implement book file upload API
      // For now, simulate upload
      setTimeout(() => {
        setBookUploadStatus({ type: 'success', message: 'Book file uploaded successfully!' });
        setUploadedBookFile({
          name: file.name,
          size: file.size
        });
        setUploadingBook(false);
        setTimeout(() => setBookUploadStatus(null), 3000);
      }, 1500);
    } catch (error) {
      setBookUploadStatus({ type: 'error', message: error.message || 'Failed to upload book file' });
      setUploadingBook(false);
    } finally {
      e.target.value = '';
    }
  };

  const handleCoverImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (images only)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setCoverUploadStatus({ type: 'error', message: 'Please upload a valid image file (JPG or PNG).' });
      return;
    }

    // Validate file size (5MB max for images)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setCoverUploadStatus({ type: 'error', message: 'Image size exceeds 5MB limit.' });
      return;
    }

    setUploadingCover(true);
    setCoverUploadStatus({ type: 'uploading', message: 'Uploading cover image...' });

    try {
      const response = await authService.uploadCoverImage(file);
      setCoverUploadStatus({ type: 'success', message: 'Cover image uploaded successfully!' });
      setUploadedCoverImage({
        name: response.originalName || response.fileName,
        preview: response.url || `https://m2ebooks.com/php_backend/${response.filePath}`
      });
      
      // Update user object in localStorage and parent state
      const updatedUser = { ...user, coverImage: response.filePath };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Update parent component state if callback provided
      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }
      
      setUploadingCover(false);
      setTimeout(() => setCoverUploadStatus(null), 3000);
    } catch (error) {
      setCoverUploadStatus({ type: 'error', message: error.message || 'Failed to upload cover image' });
      setUploadingCover(false);
    } finally {
      e.target.value = '';
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      setUploadStatus({ type: 'error', message: 'Please upload a PDF file only.' });
      return;
    }

    // Validate file size (50MB max)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      setUploadStatus({ type: 'error', message: 'File size exceeds 50MB limit.' });
      return;
    }

    setUploading(true);
    setUploadStatus({ type: 'uploading', message: 'Uploading PDF...' });

    try {
      const response = await authService.uploadPDF(file);
      setUploadStatus({ type: 'success', message: 'PDF uploaded successfully!' });
      setUploadedFile({
        path: response.filePath,
        name: response.originalName || response.fileName
      });
      
      // Update user object in localStorage and parent state
      const updatedUser = { ...user, pdf_document: response.filePath };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Update parent component state if callback provided
      if (onUserUpdate) {
        onUserUpdate(updatedUser);
      }
      
      // Clear status after 3 seconds
      setTimeout(() => {
        setUploadStatus(null);
      }, 3000);
    } catch (error) {
      setUploadStatus({ type: 'error', message: error.message || 'Failed to upload PDF' });
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (!isAuthenticated) {
    return (
      <ProfileContainer>
        <ProfileContent>
          <EmptyState>
            <h3>Please sign in</h3>
            <p>You need to be logged in to view your profile.</p>
            <Link to="/login" style={{ color: '#2563eb', textDecoration: 'none' }}>
              Sign In
            </Link>
          </EmptyState>
        </ProfileContent>
      </ProfileContainer>
    );
  }

  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase();

  return (
    <ProfileContainer>
      <ProfileContent>
        <ProfileHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <BackButton to="/">
              ← Back to Home
            </BackButton>
            <ProfileTitle>My Profile</ProfileTitle>
          </div>
          {onLogout && (
            <SignOutButton onClick={onLogout}>
              <FiLogOut size={16} />
              Sign Out
            </SignOutButton>
          )}
        </ProfileHeader>

        <ProfileGrid>
          <ProfileSidebar>
            <ProfileAvatar>
              {initials || <FiUser size={48} />}
            </ProfileAvatar>
            <ProfileName>{user?.firstName} {user?.lastName}</ProfileName>
            <ProfileEmail>{user?.email}</ProfileEmail>
            
            {/* <ProfileStats>
              <StatItem>
                <FiBookOpen className="icon" size={20} />
                <div className="content">
                  <div className="label">Books Purchased</div>
                  <div className="value">12</div>
                </div>
              </StatItem>
              
              <StatItem>
                <FiHeart className="icon" size={20} />
                <div className="content">
                  <div className="label">Wishlist Items</div>
                  <div className="value">8</div>
                </div>
              </StatItem>
              
              <StatItem>
                <FiShoppingBag className="icon" size={20} />
                <div className="content">
                  <div className="label">Total Spent</div>
                  <div className="value">$156.99</div>
                </div>
              </StatItem>
            </ProfileStats> */}
          </ProfileSidebar>

          <ProfileMain>
            <ProfileSection>
              <SectionHeader>
                <SectionTitle>
                  <FiUser />
                  Personal Information
                </SectionTitle>
                {!isEditing && (
                  <EditButton onClick={() => setIsEditing(true)}>
                    <FiEdit3 size={16} />
                    Edit
                  </EditButton>
                )}
              </SectionHeader>

              <FormGrid>
                <InputGroup>
                  <Label>First Name</Label>
                  <Input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                </InputGroup>
                
                <InputGroup>
                  <Label>Last Name</Label>
                  <Input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                </InputGroup>
              </FormGrid>
              
              <InputGroup>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </InputGroup>
              
              <InputGroup>
                <Label>Amazon eBook Listing link</Label>
                <Input
                  type="url"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="Enter your Amazon eBook listing URL"
                />
              </InputGroup>
            </ProfileSection>

            <ProfileSection>
              <SectionHeader>
                <SectionTitle>
                  <FiBook />
                  Book Upload Information
                </SectionTitle>
              </SectionHeader>

              <InputGroup>
                <Label>Book Title</Label>
                <Input
                  type="text"
                  name="bookTitle"
                  value={formData.bookTitle}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="Enter book title"
                />
              </InputGroup>

              <FormGrid>
                <InputGroup>
                  <Label>Author Name</Label>
                  <Input
                    type="text"
                    name="authorName"
                    value={formData.authorName}
                    onChange={handleChange}
                    disabled={!isEditing}
                    placeholder="Enter author name"
                  />
                </InputGroup>
                
                <InputGroup>
                  <Label>Genre / Category</Label>
                  <Input
                    type="text"
                    name="genre"
                    value={formData.genre}
                    onChange={handleChange}
                    disabled={!isEditing}
                    placeholder="e.g., Fiction, Mystery, Romance"
                  />
                </InputGroup>
              </FormGrid>
              
              <InputGroup>
                <Label>Description / Synopsis</Label>
                <TextArea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="Enter book description or synopsis..."
                  rows={5}
                />
              </InputGroup>

              {/* <InputGroup>
                <Label>Upload Book File</Label>
                <FileUploadContainer>
                  <FileInput
                    type="file"
                    id="book-upload"
                    accept=".pdf,.epub,.mobi,application/pdf,application/epub+zip,application/x-mobipocket-ebook"
                    onChange={handleBookFileChange}
                    disabled={uploadingBook || !isEditing}
                  />
                  <FileInputLabel htmlFor="book-upload" disabled={uploadingBook || !isEditing}>
                    <FiUpload size={18} />
                    {uploadingBook ? 'Uploading...' : 'Choose Book File'}
                  </FileInputLabel>
                  <p style={{ marginTop: '12px', color: '#6b7280', fontSize: '0.875rem' }}>
                    Maximum file size: 50MB. Supported formats: PDF, EPUB, MOBI
                  </p>

                  {bookUploadStatus && (
                    <UploadStatus className={bookUploadStatus.type}>
                      {bookUploadStatus.message}
                    </UploadStatus>
                  )}

                  {uploadedBookFile && (
                    <FileInfo>
                      <FileName>
                        <FiFile size={20} color="#2563eb" />
                        <span>{uploadedBookFile.name}</span>
                        {uploadedBookFile.size && (
                          <FileSize>({formatFileSize(uploadedBookFile.size)})</FileSize>
                        )}
                      </FileName>
                    </FileInfo>
                  )}
                </FileUploadContainer>
              </InputGroup> */}

              <InputGroup>
                <Label>Upload Book Cover Image</Label>
                <FileUploadContainer>
                  <FileInput
                    type="file"
                    id="cover-upload"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleCoverImageChange}
                    disabled={uploadingCover || !isEditing}
                  />
                  <FileInputLabel htmlFor="cover-upload" disabled={uploadingCover || !isEditing}>
                    <FiImage size={18} />
                    {uploadingCover ? 'Uploading...' : 'Choose Book Cover Image'}
                  </FileInputLabel>
                  <p style={{ marginTop: '12px', color: '#6b7280', fontSize: '0.875rem' }}>
                    Maximum file size: 5MB. Supported formats: JPG, PNG
                  </p>

                  {coverUploadStatus && (
                    <UploadStatus className={coverUploadStatus.type}>
                      {coverUploadStatus.message}
                    </UploadStatus>
                  )}

                  {uploadedCoverImage && (
                    <div style={{ marginTop: '16px' }}>
                      <FileInfo>
                        <FileName>
                          <FiImage size={20} color="#2563eb" />
                          <span>{uploadedCoverImage.name}</span>
                        </FileName>
                      </FileInfo>
                      {uploadedCoverImage.preview && (
                        <img 
                          src={uploadedCoverImage.preview} 
                          alt="Cover preview" 
                          style={{ 
                            marginTop: '12px', 
                            maxWidth: '200px', 
                            maxHeight: '300px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }} 
                        />
                      )}
                    </div>
                  )}
                </FileUploadContainer>
              </InputGroup>
            </ProfileSection>

            <ProfileSection>
              <SectionHeader>
                <SectionTitle>
                  <FiFile />
                  PDF Document Upload
                </SectionTitle>
              </SectionHeader>

              <FileUploadContainer>
                <FileInput
                  type="file"
                  id="pdf-upload"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                <FileInputLabel htmlFor="pdf-upload" disabled={uploading}>
                  <FiUpload size={18} />
                  {uploading ? 'Uploading...' : 'Choose PDF File to Upload'}
                </FileInputLabel>
                
                <p style={{ marginTop: '12px', color: '#6b7280', fontSize: '0.875rem' }}>
                  Maximum file size: 50MB. Supported format: PDF only.
                </p>

                {uploadStatus && (
                  <UploadStatus className={uploadStatus.type}>
                    {uploadStatus.message}
                  </UploadStatus>
                )}

                {uploadedFile && (
                  <FileInfo>
                    <FileName>
                      <FiFile size={20} color="#2563eb" />
                      <span>{uploadedFile.name}</span>
                    </FileName>
                    <DownloadLink 
                      href={`https://m2ebooks.com/php_backend/${uploadedFile.path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FiDownload size={16} />
                      Download
                    </DownloadLink>
                  </FileInfo>
                )}
              </FileUploadContainer>
            </ProfileSection>

            {isEditing && (
              <FormActions style={{ marginTop: '32px', justifyContent: 'flex-end' }}>
                <SaveButton onClick={handleSave} disabled={loading}>
                  <FiSave size={16} />
                  {loading ? 'Saving...' : 'Save Changes'}
                </SaveButton>
                <CancelButton onClick={handleCancel}>
                  <FiX size={16} />
                  Cancel
                </CancelButton>
              </FormActions>
            )}
          </ProfileMain>
        </ProfileGrid>
      </ProfileContent>
    </ProfileContainer>
  );
};

export default UserProfile;
